import React, {useState, useRef, useEffect, useCallback, useMemo} from 'react';
import ReactDOM from 'react-dom';

export default function CustomMuiTooltip({
  children,
  content,                     // Content of the tooltip
  placement = 'bottom', // Tooltip position ('left', 'right', 'top', 'bottom')
  style = {},               // Additional styles for the wrapper
  hideTimeout = 0, // Timeout that allows moving cursor into tooltip
  constrainToViewport = false // Prevents tooltip from overflowing the viewport
}) {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({top: 0, left: 0});

  const hideTimeoutRef = useRef(null);
  const tooltipRef = useRef(null);
  const containerRef = useRef(null);

  // Calculate and set the tooltip position
  const calculatePosition = useCallback(() => {
    if (!tooltipRef.current || !containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();

    let top = 0;
    let left = 0;

    switch (placement) {
      case 'left':
        top = containerRect.top + (containerRect.height - tooltipRect.height) / 2;
        left = containerRect.left - tooltipRect.width - 6;
        if (left < 0) {
          left = containerRect.right + 5;
        }
        break;
      case 'right':
        top = containerRect.top + (containerRect.height - tooltipRect.height) / 2;
        left = containerRect.right + 5;
        if (left + tooltipRect.width > window.innerWidth) {
          left = containerRect.left - tooltipRect.width - 6;
        }
        break;
      case 'top':
        top = containerRect.top - tooltipRect.height - 6;
        left = containerRect.left + (containerRect.width - tooltipRect.width) / 2;
        if (top < 0) {
          top = containerRect.bottom + 6;
        }
        break;
      case 'bottom':
        top = containerRect.bottom + 5;
        left = containerRect.left + (containerRect.width - tooltipRect.width) / 2;
        if (top + tooltipRect.height > window.innerHeight) {
          top = containerRect.top - tooltipRect.height - 6;
        }
        break;
    }

    if (left < 0) {
      left = 5; // Keep it within the viewport
    } else if (left + tooltipRect.width > window.innerWidth) {
      left = window.innerWidth - tooltipRect.width - 6;
    }

    setPosition({top: top, left: left});
  }, [placement]);

  const tooltipContainer = useMemo(() => document.createElement('div'), []);

  const tooltipContent = useMemo(() =>
    <div
      className='thin-scrollbar'
      ref={tooltipRef}
      style={{
        backgroundColor: '#FAFAFA',
        color: '#666',
        padding: '8px',
        borderRadius: '5px',
        border: '1px solid #ccc',
        boxShadow: '1px 1px 4px #ccc',
        fontSize: '13px',
        position: 'fixed',
        zIndex: 9000,
        ...position,
        ...style,
        ...(constrainToViewport && {
          maxHeight: `calc(100vh - ${position.top}px - 8px)`,
          overflowY: 'auto',
          boxSizing: 'border-box',
        }),
        display: visible ? 'block' : 'none'
      }}
    >
      {content}
    </div>
  , [visible, position]);

  useEffect(() => {
    document.body.appendChild(tooltipContainer);
    return () => document.body.removeChild(tooltipContainer);
  }, [tooltipContainer]);

  const showTooltip = () => {
    clearTimeout(hideTimeoutRef.current);
    setVisible(true);
  };

  const hideTooltip = () => {
    hideTimeoutRef.current = setTimeout(() => setVisible(false), hideTimeout);
  };

  useEffect(() => {
    if (visible) {
      calculatePosition();
      window.addEventListener('resize', calculatePosition);
      return () => window.removeEventListener('resize', calculatePosition);
    }
  }, [visible, calculatePosition]);

  return (<span
    ref={containerRef}
    onMouseEnter={showTooltip}
    onMouseLeave={hideTooltip}
    onClick={() => setVisible(false)}
    //style={{display: 'inline-block'}}
  >
    {children}
    {visible && ReactDOM.createPortal(tooltipContent, tooltipContainer)}
  </span>);
};
